import React, { useContext, useMemo, useState } from "react";
import { IdentifierMappingContext } from "../IdentifierMappingContext";
import { InputAdornment, TextField } from "@mui/material";
import { Search } from "@material-ui/icons";
import { Badge } from "../../common/Badge";
import { HORIZON } from "../../../constants/horizon";
import MixpanelService from "../../utils/MixpanelService";

// Unknown first, followed by name alphabetic.
const pharmaciesSort = (a, b) => {
  if (a.id === "unknown") return -1;
  if (b.id === "unknown") return 1;

  return a.name.localeCompare(b.name);
};

// sort first by DEA, HIN, NPI then alphabetic
const identifierAssocSort = (a, b) => {
  const sortOrder = HORIZON.IDENITIFER_SORT_ORDER;
  const fallbackIndex = sortOrder.length;

  let indexA = sortOrder.indexOf(a.identifier_type);
  let indexB = sortOrder.indexOf(b.identifier_type);

  if (indexA === -1) indexA = fallbackIndex;
  if (indexB === -1) indexB = fallbackIndex;

  if (indexA !== indexB) {
    return indexA - indexB;
  }

  return a.identifier.localeCompare(b.identifier);
};

export const PharmaciesTab = ({ pharmacyGroupDetails }) => {
  const {
    allGroupNames,
    setPharmacyGroupTab,
    setCategorizeIdentifierModalOpen,
    activePharmacyGroup,
    setAddIdentifierModalOpen,
    setTransferIdentifierModalOpen,
    setTransferAllIdentifiersModalOpen,
    setCategorizeAllIdentifiersModalOpen,
    setRemoveIdentifierModalOpen,
  } = useContext(IdentifierMappingContext);
  const [searchInput, setSearchInput] = useState("");

  const pharmacies = pharmacyGroupDetails.pharmacies.sort(pharmaciesSort);

  const filteredPharmacies = useMemo(
    () =>
      pharmacies.filter((p) => {
        if (searchInput.trim() === "") {
          return true;
        }

        const normalizedInput = searchInput.toLowerCase();

        if (p.name.toLowerCase().includes(normalizedInput)) {
          return true;
        }

        if (p.address?.toLowerCase().includes(normalizedInput)) {
          return true;
        }

        return p.identifier_associations.find((ia) =>
          ia.identifier.toLowerCase().includes(normalizedInput)
        );
      }),
    [searchInput, pharmacies]
  );

  const isUnassignedGroup = activePharmacyGroup === "unassigned";

  const hasAvailablePharmacyGroupOptions = allGroupNames.length > 1;

  return (
    <div className="mt-4">
      <div className="t--500 mb-3">Pharmacies and Identifiers</div>

      <div className="flex align-items-center justify-content-between mb-3 gap--16">
        <TextField
          className="w--full"
          label=""
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
          }}
          onClick={() => {
            MixpanelService.track("esp_horizon_mgn_id_search_accessed");
          }}
          onBlur={() => MixpanelService.track("esp_horizon_mgn_id_search_exited")}
          placeholder="Search for a Pharmacy or Identifier"
          size="small"
          sx={{
            "& .MuiInputBase-root": {
              height: 36,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />

        {!isUnassignedGroup ? (
          <button
            className="button button--white flex-shrink-0"
            onClick={() => {
              MixpanelService.track("esp_horizon_aim_add_identifier");
              setAddIdentifierModalOpen(true);
            }}
            style={{ height: 40 }}
          >
            Add Identifier
          </button>
        ) : null}
      </div>

      <div className="container-fluid row-border-rounded">
        <div className="row align-items-center py-3 bg--neutrals-gray-3 t--sm border--rounded-top">
          <div className="col t--500">Pharmacy</div>
          <div className="col t--500">Associated Identifiers</div>
          <div className="col"></div>
        </div>
        {filteredPharmacies.map((pharmacy) => {
          const badges = ["dea", "hin", "npi"].map((type) => {
            const count = pharmacy.identifier_associations.filter(
              (ia) => ia.identifier_type === type
            ).length;
            return `${count > 0 ? count : "--"} ${type.toUpperCase()}`;
          });

          const hasAllPendingTransfers = !pharmacy.identifier_associations.find(
            (ia) => !ia.has_mapping_request
          );

          const showTransferAllBtn =
            pharmacy.identifier_associations.length > 1 &&
            hasAvailablePharmacyGroupOptions &&
            !hasAllPendingTransfers &&
            !isUnassignedGroup;

          return (
            <React.Fragment key={pharmacy.name + pharmacy.address}>
              <div className="row align-items-center py-3 bg--light-navy row-border-bottom">
                <div className="col">
                  <div className="t--500 t--sm mb-1">{pharmacy.name}</div>
                  <div className="t--secondary t--sm">{pharmacy.address}</div>
                </div>
                <div className="col">
                  <div className="flex gap--4 align-items-center">
                    {badges.map((badge) => (
                      <div
                        className={`badge border--none shadow-none bg--light-blue t--blue border--rounded`}
                        key={badge}
                      >
                        {badge}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="col flex justify-content-end align-items-center">
                  {pharmacy.identifier_associations.length == 0 && !isUnassignedGroup ? (
                    <button
                      className="button button--white button--md"
                      onClick={() => setAddIdentifierModalOpen({ pharmacy })}
                    >
                      Add Identifier
                    </button>
                  ) : null}
                  {showTransferAllBtn ? (
                    <button
                      className="button button--white button--md"
                      onClick={() => {
                        setTransferAllIdentifiersModalOpen({ pharmacy });
                        MixpanelService.track("esp_horizon_transfer_all_started");
                      }}
                    >
                      Transfer All
                    </button>
                  ) : null}
                </div>
              </div>
              {pharmacy.identifier_associations.length == 0 ? (
                <div className="flex align-items-center justify-content-center bg--white py-3 t--sm col-center row-border-bottom">
                  <div className="t--secondary">No Identifiers Assigned</div>
                </div>
              ) : null}
              {pharmacy.identifier_associations.sort(identifierAssocSort).map((ia) => {
                const hasPendingRequest = !!ia.has_mapping_request;

                const showTransferBtn =
                  hasPendingRequest || isUnassignedGroup || hasAvailablePharmacyGroupOptions;

                const handleTransferClick = () => {
                  if (hasPendingRequest) {
                    MixpanelService.track("esp_horizon_transfer_identifier_started");
                    setPharmacyGroupTab("pending_requests");
                  } else {
                    if (isUnassignedGroup) {
                      MixpanelService.track("esp_horizon_categorize_identifier_started");
                      setCategorizeIdentifierModalOpen({ identifier_association: ia, pharmacy });
                    } else {
                      MixpanelService.track("esp_horizon_transfer_identifier_started");
                      setTransferIdentifierModalOpen({ identifier_association: ia, pharmacy });
                    }
                  }
                };

                const handleRemoveClick = () => {
                  setRemoveIdentifierModalOpen({ identifier_association: ia, pharmacy });
                };

                return (
                  <div
                    key={ia.identifier + ia.identifier_type}
                    className="row bg--white py-2 t--sm col-center row-border-bottom"
                  >
                    <div className="col t--500">{ia.identifier}</div>
                    <div className="col gap--16">
                      <div>{ia.identifier_type.toUpperCase()}</div>
                      {hasPendingRequest ? <Badge color="purple">Pending Request</Badge> : null}
                    </div>
                    <div className="col flex justify-content-end align-items-center gap--8">
                      {showTransferBtn ? (
                        <button
                          className="button button--white button--md"
                          onClick={handleTransferClick}
                        >
                          {hasPendingRequest
                            ? "View Request"
                            : isUnassignedGroup
                              ? "Categorize"
                              : "Transfer"}
                        </button>
                      ) : null}

                      {!isUnassignedGroup ? (
                        <button
                          className="button button--white button--md btn--sqr"
                          disabled={hasPendingRequest}
                          onClick={handleRemoveClick}
                        >
                          <span className="material-symbols-rounded">delete</span>
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
